/**
 * Accessibility patches for the Enzuzo widget (privacy policy, terms of
 * service, do-not-sell-my-information pages).
 *
 * The page body Shopify serves is just `<div id="__enzuzo-root"></div>` plus
 * Enzuzo's own script tag; Enzuzo hydrates client-side. Live-DOM inspection
 * (Playwright, privacy/ToS/do-not-sell pages) shows Enzuzo does NOT render
 * its content as a descendant of `#__enzuzo-root` - it renders an
 * `<astro-island>` (privacy, do-not-sell) or its own `<main class="ez-container">`
 * (terms-of-service) as a SIBLING of the root div, inside the same parent
 * container. `#__enzuzo-root`'s parent is therefore the closest stable
 * element that actually wraps the rendered widget on every page type, and is
 * what we scope the MutationObserver and queries to. `#__enzuzo-root` itself
 * is only used as the cheap "is the widget on this page" bail-out check.
 *
 * We do not touch the main-inside-main issue on terms-of-service; that is
 * fixed by assigning the page's template to `page.enzuzo` in the admin.
 *
 * Findings fixed (see .superpowers/sdd/2026-09-22-ada-third-party-patches):
 *  1. Empty `h2[ez-flat-title]` headings (ARIA "headings should not be
 *     empty"). One appears on privacy and terms-of-service; it still takes
 *     up a small (blank) box, so we hide it from the accessibility tree
 *     with aria-hidden instead of removing it, to guarantee no visual change.
 *  2. `fieldset[ez-dsar-fieldset]` (the data-request form) has its `<legend>`
 *     nested inside `div[ez-dsar-option-container]`, not as the fieldset's
 *     first child, so it does not name the group (SortSite H71, twice: "no
 *     legend" and "legend must be the first child"). The nested legend is a
 *     cell of Enzuzo's two-column grid, so moving it would change the layout.
 *     Instead we:
 *       - point the fieldset's aria-labelledby at the nested element (this
 *         is what screen readers use),
 *       - swap that nested <legend> for a <div> with the same attributes and
 *         children, so there's no misplaced second legend,
 *       - insert a `display: none` <legend> with the same text as the
 *         fieldset's first child, which is what SortSite checks for.
 *     `display: none` rather than tw-sr-only: SortSite counts text in a
 *     clipped sr-only element as blank (see SwatchHelperToA11y.js). The form
 *     doesn't re-render this part on input or validation, and the swap
 *     rendered pixel-identical on live (checked with every request blocked).
 *  3. Three "website" links on privacy with different hrefs (Colorado,
 *     Virginia, Connecticut attorney-general complaint pages) share the
 *     same visible text. We append an sr-only " – {state}" (en dash)
 *     suffix, read from the "If you are based in {state}, ..." text
 *     immediately before each link.
 */

const ANCHOR_SELECTOR = "#__enzuzo-root";
const EMPTY_HEADING_SELECTOR = "h2[ez-flat-title]";
const DSAR_FIELDSET_SELECTOR = "fieldset[ez-dsar-fieldset]";
const SR_ONLY_CLASS = "tw-sr-only";
const LINK_CONTEXT_CLASS = "a11y-enzuzo-link-context";
const HIDDEN_HEADING_MARKER_ATTR = "data-a11y-ez-hidden";
const FIRST_LEGEND_MARKER_ATTR = "data-a11y-ez-legend";
const LEGEND_SOURCE_MARKER_ATTR = "data-a11y-ez-legend-source";
const BASED_IN_PATTERN = /based in ([^,.]+)/gi;

let legendIdSequence = 0;
const observedScopes = new WeakSet();

export const patchEnzuzoA11y = () => {
    const anchor = document.querySelector(ANCHOR_SELECTOR);
    if (!anchor || !anchor.parentElement) {
        return;
    }

    const scope = anchor.parentElement;

    patchScope(scope);

    if (observedScopes.has(scope)) {
        return;
    }
    observedScopes.add(scope);

    const observer = new MutationObserver(() => patchScope(scope));
    observer.observe(scope, { childList: true, subtree: true });
};

const patchScope = (scope) => {
    hideEmptyFlatHeadings(scope);
    nameDsarFieldsets(scope);
    disambiguateDuplicateLinks(scope);
};

/**
 * Hides only headings that are actually empty, and marks the ones we hide
 * (as opposed to a vendor-set aria-hidden we'd otherwise have no way to tell
 * apart) so a later pass can undo it if Enzuzo re-renders the heading with
 * real text (e.g. a locale reboot that fills in a title that was
 * momentarily blank).
 */
const hideEmptyFlatHeadings = (scope) => {
    scope.querySelectorAll(EMPTY_HEADING_SELECTOR).forEach((heading) => {
        const isEmpty = heading.textContent.trim() === "";
        const hiddenByUs = heading.getAttribute(HIDDEN_HEADING_MARKER_ATTR) === "true";

        if (isEmpty) {
            if (hiddenByUs) {
                return;
            }
            heading.setAttribute("aria-hidden", "true");
            heading.setAttribute(HIDDEN_HEADING_MARKER_ATTR, "true");
            return;
        }

        if (hiddenByUs) {
            heading.removeAttribute("aria-hidden");
            heading.removeAttribute(HIDDEN_HEADING_MARKER_ATTR);
        }
    });
};

const nameDsarFieldsets = (scope) => {
    scope.querySelectorAll(DSAR_FIELDSET_SELECTOR).forEach((fieldset) => {
        const source = findLegendSource(fieldset);
        if (!source) {
            return;
        }
        if (!source.id) {
            source.id = `ez-dsar-legend-${legendIdSequence++}`;
        }
        if (fieldset.getAttribute("aria-labelledby") !== source.id) {
            fieldset.setAttribute("aria-labelledby", source.id);
        }
        ensureFirstChildLegend(fieldset, source);
    });
};

/**
 * The element that carries the group's visible label: Enzuzo's nested
 * <legend>, swapped for a <div> the first time we see it (and again if
 * Enzuzo ever re-renders a fresh <legend>). Returns null when the fieldset
 * has no label at all, and leaves a fieldset alone when Enzuzo already puts
 * its legend first (nothing to fix).
 */
const findLegendSource = (fieldset) => {
    const vendorLegend = Array.from(fieldset.querySelectorAll("legend")).find(
        (legend) => !legend.hasAttribute(FIRST_LEGEND_MARKER_ATTR)
    );
    if (vendorLegend) {
        if (vendorLegend === fieldset.firstElementChild) {
            return null;
        }
        return replaceWithDiv(vendorLegend);
    }
    return fieldset.querySelector(`[${LEGEND_SOURCE_MARKER_ATTR}]`);
};

const replaceWithDiv = (legend) => {
    const div = document.createElement("div");
    Array.from(legend.attributes).forEach((attribute) => div.setAttribute(attribute.name, attribute.value));
    div.setAttribute(LEGEND_SOURCE_MARKER_ATTR, "");
    while (legend.firstChild) {
        div.appendChild(legend.firstChild);
    }
    legend.replaceWith(div);
    return div;
};

// Only writes when something is actually missing or stale, so the
// MutationObserver that re-runs this after our own changes settles instead
// of looping.
const ensureFirstChildLegend = (fieldset, source) => {
    const text = source.textContent.replace(/\s+/g, " ").trim();
    let legend = fieldset.querySelector(`:scope > legend[${FIRST_LEGEND_MARKER_ATTR}]`);
    if (!legend) {
        legend = document.createElement("legend");
        legend.setAttribute(FIRST_LEGEND_MARKER_ATTR, "");
        legend.hidden = true;
        legend.style.display = "none";
    }
    if (legend.textContent !== text) {
        legend.textContent = text;
    }
    if (fieldset.firstElementChild !== legend) {
        fieldset.insertBefore(legend, fieldset.firstChild);
    }
};

const disambiguateDuplicateLinks = (scope) => {
    const groups = new Map();
    scope.querySelectorAll("a[href]").forEach((link) => {
        const label = getVisibleLabel(link);
        if (!label) {
            return;
        }
        if (!groups.has(label)) {
            groups.set(label, []);
        }
        groups.get(label).push(link);
    });

    groups.forEach((links) => {
        const distinctHrefs = new Set(links.map((link) => link.href));
        if (distinctHrefs.size < 2) {
            return;
        }
        links.forEach((link) => {
            if (link.querySelector(`.${LINK_CONTEXT_CLASS}`)) {
                return;
            }
            appendLinkContext(link);
        });
    });
};

// Excludes our own previously-appended context span so a link we already
// patched still groups with its siblings by the vendor's original visible
// text ("website"), not by the text we added ("website – Colorado").
const getVisibleLabel = (link) => {
    const clone = link.cloneNode(true);
    clone.querySelectorAll(`.${LINK_CONTEXT_CLASS}`).forEach((span) => span.remove());
    return clone.textContent.replace(/\s+/g, " ").trim().toLowerCase();
};

const appendLinkContext = (link) => {
    const context = getLinkContext(link);
    if (!context) {
        // No "based in {state}" text and no usable hostname (e.g. a mailto:/
        // tel: link) - nothing to disambiguate with, so leave the link alone
        // rather than append a bare " – ".
        return;
    }
    const span = document.createElement("span");
    span.className = `${SR_ONLY_CLASS} ${LINK_CONTEXT_CLASS}`;
    span.textContent = ` – ${context}`;
    link.appendChild(span);
};

/**
 * Reads the text within the link's parent up to (not including) the
 * preceding <br> - which is how Enzuzo separates these sentences instead of
 * wrapping each one in its own <p> - and pulls the state name out of the
 * LAST "... based in {State}, ..." in that stretch (a sentence could
 * plausibly mention "based in" more than once before the link; the
 * occurrence closest to the link is the one that actually qualifies it).
 * Falls back to the link's hostname when nothing matches, and to nothing at
 * all when that hostname is also empty (mailto:/tel: links).
 */
const getLinkContext = (link) => {
    let precedingText = "";
    let node = link.previousSibling;
    while (node) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") {
            break;
        }
        precedingText = (node.textContent || "") + precedingText;
        node = node.previousSibling;
    }

    const matches = [...precedingText.matchAll(BASED_IN_PATTERN)];
    if (matches.length > 0) {
        return matches[matches.length - 1][1].trim();
    }

    try {
        return new URL(link.href).hostname.replace(/^www\./, "");
    } catch (error) {
        return link.hostname || "";
    }
};
